import React, {useEffect, useRef, useState} from 'react';
import {USMapType} from '../../model/geographic/USMapType';
import USMapState from "../../model/geographic/USMapState";
import {FeatureCollection, Geometry} from "geojson";
import USElectionData from "../../model/data/USElectionData";
import {useDashboard} from '../../model/DashboardContext';
import {fetchData, prepareStateFeatures} from "./USMapUtils";
import {render_map} from "./USMapRenderer";
import {USMapInteractionData, USMapTooltip} from "./USMapToolTip";
import { ColorLegend } from './ColorLegend'; // Import the ColorLegend
import * as d3 from 'd3';
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';

const USMap: React.FC<{ xScale: number, yScale: number }> = ({ xScale, yScale }) => {
    const { dashboard, updateSelectedYear, updateSelectedStates } = useDashboard();
    const [usGeoData, setUsGeoData] = useState<FeatureCollection<Geometry> | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [mapState, setMapState] = useState(() => new USMapState(
        0,
        new USElectionData(0, []))
    );
    const [tooltipData, setTooltipData] = useState<USMapInteractionData | null>(null);

    useEffect(() => {
        if (!usGeoData) {
            fetchData().then(setUsGeoData);
        }
    }, [usGeoData]);

    useEffect(() => {
        if (!usGeoData || !dashboard?.isInitialized) return;

        if (mapState.year === 0) {
            setMapState(new USMapState(dashboard.year, dashboard.selectedElectionData));
            return;
        }

        // if (mapState.year !== dashboard.year) {
        //     const newMapState = new USMapState(dashboard.year, dashboard.selectedElectionData);
        //     newMapState.setMapType(mapState.mapType);
        //     setMapState(newMapState);
        // }

        const stateFeatures = prepareStateFeatures(usGeoData);
        render_map(usGeoData, svgRef, mapState, stateFeatures, xScale, yScale, dashboard, updateSelectedStates, setTooltipData);
    }, [usGeoData, mapState, xScale, yScale, dashboard, updateSelectedStates, dashboard?.selectedStates]);

    const handleMapTypeChange = (event: SelectChangeEvent<USMapType>) => {
        const newMapType = event.target.value as USMapType;
        setMapState((prevState) => {
            const newState = new USMapState(prevState.year, prevState.data);
            newState.setMapType(newMapType);
            return newState;
        });
    };

    const handleYearChange = (event: SelectChangeEvent<Number>) => {
        if (!dashboard) return;
        const newYear = +event.target.value;
        dashboard.setYear(newYear);
        const newMapState = new USMapState(newYear, dashboard.selectedElectionData);
        newMapState.setMapType(mapState.mapType);
        setMapState(newMapState);
        updateSelectedYear(newYear);
        console.log(dashboard.year)

    };

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Color Legend on the left side */}
        <div
    style={{
      position: "absolute",
      marginLeft: "0px", // Adjust this to position the text
      top: "55%",
      transform: "translateY(-50%) rotate(-90deg)", // Rotate text vertically
      transformOrigin: "left center", // Ensures the text is aligned
      fontSize: "11px",
      fontWeight: 500,
      whiteSpace: "nowrap" // Keeps the text on one line
    }}
  >
    Winning Party's Percentage Lead (Dem: negative, Rep: positive)
  </div>
              <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "-20px",
                        marginLeft: "25px"
                    }}>
                    
        <ColorLegend
            height={200} // Set height based on map dimensions
            width={10}   // Set appropriate width for the legend
            colorScale={dashboard?.colorScale || d3.scaleQuantize<string, never>()}
        />
        </div>
        <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative', width: '100%' }}>
                <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '10px', padding: '10px' }}>
                    <MapTypeSelector value={mapState.mapType} onChange={handleMapTypeChange} />
                    <YearSelector value={dashboard?.year || 0} onChange={handleYearChange} />
                </div>
            </div>
            <MapTitle />
            <svg ref={svgRef} width="800" height="460" style={{ marginLeft: '-60px' }}></svg>
            <USMapTooltip data={tooltipData?.data || []} position={tooltipData?.position || { x: 0, y: 0 }} width={800} height={447} />
        </div>
        </div>

    );
};

const MapTitle: React.FC = () => (
    <text style={{marginLeft:-50, fontSize: 15, fontWeight: 800, marginTop: 20 }}>
        Dominance of votes in each state over the years
    </text>
);

const MapTypeSelector: React.FC<{ value: USMapType, onChange: (event: SelectChangeEvent<USMapType>) => void }> = ({ value, onChange }) => (
    <>
        <label htmlFor="USMapType"></label>
        {/* <select id="USMapType" value={value} onChange={onChange}>
            {Object.values(USMapType).map((type) => (
                <option key={type} value={type}>{type}</option>
            ))}
        </select> */}

        <FormControl 
        variant="outlined" 
        style={{ position: "absolute", zIndex: 10, marginLeft: "-320px", marginTop: "-10px", width: 200, height: 30}}
      >
        <Select
        id="USMapType" value={value} onChange={onChange}
        //   onChange={(e) => setSortBy(e.target.value)}
          displayEmpty
          variant="standard"
          sx={{ fontSize: '0.8rem' }}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'top',   // Anchor to the top of the input
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'bottom', // Dropdown grows upwards
                        horizontal: 'left',
                    },
                }}
        >
            {Object.values(USMapType).map((type) => (
          <MenuItem key={type} value={type}>
            {type}
        </MenuItem>
        ))}
        </Select>
      </FormControl>
    </>
);

const YearSelector: React.FC<{ value: number, onChange: (event: SelectChangeEvent<Number>) => void }> = ({ value, onChange }) => {
    const validElectionYears = [1976, 1980, 1984, 1988, 1992, 1996, 2000, 2004, 2008, 2012, 2016, 2020];
    return (
        <>
            <label htmlFor="ElectionYear"></label>
            {/* <select id="ElectionYear" value={value} onChange={onChange}>
                {validElectionYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                ))}
            </select> */}
                    <FormControl 
        variant="outlined" 
        style={{ position: "absolute", zIndex: 10, marginLeft: "-100px", marginTop: "-10px", width: 100, height: 30}}
      >
        <Select
        id="ElectionYear" value={value} onChange={onChange}
        //   onChange={(e) => setSortBy(e.target.value)}
          displayEmpty
          variant="standard"
          sx={{ fontSize: '0.8rem' }}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'top',   // Anchor to the top of the input
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'bottom', // Dropdown grows upwards
                        horizontal: 'left',
                    },
                }}
        >
            {validElectionYears.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
        </MenuItem>
        ))}
        </Select>
      </FormControl>
        </>
    );
};

export default USMap;